import { showError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  isRecapPublic,
  normalizeRecapAccessLevel,
  RECAP_ACCESS_LEVELS,
  RecapPermission,
} from 'features/recaps/constants'
import {
  useDestroySharesV2Mutation,
  usePostSharesV2Mutation,
} from 'hooks/notetaker/api'
import { rootApi } from 'root-api'

const invalidateRecapPermissionsCache = (dispatch, trackedMeetingUuid) => {
  if (!trackedMeetingUuid) return

  // Do not upsert [] here. That creates a real RTK Query cache value and can hide
  // server-side grants when the share modal reuses cached permissions.
  dispatch(
    rootApi.util.invalidateTags([{ type: 'recapPermissions', id: trackedMeetingUuid }]),
  )
}

export const useAccessLevel = ({
  trackedMeetingUuid,
  recapPermission,
  share,
  isShareModalOpen,
  isShareReady,
  setLocalShare,
}) => {
  const dispatch = useDispatch()
  const [accessLevel, setAccessLevel] = useState(null)
  const [pendingAccessLevel, setPendingAccessLevel] = useState(null)
  const [postShares, { isLoading: isSavingShare }] = usePostSharesV2Mutation()
  const [destroyShares, { isLoading: isDeletingShare }] = useDestroySharesV2Mutation()

  const isAccessLevelSubmitting = isSavingShare || isDeletingShare

  // Set access level on modal open
  useEffect(() => {
    if (!isShareModalOpen || !isShareReady) return

    const shareAccessLevel = share?.accessLevel

    if (!shareAccessLevel) {
      setAccessLevel(RECAP_ACCESS_LEVELS.PRIVATE)
      return
    }

    setAccessLevel(normalizeRecapAccessLevel(shareAccessLevel))
  }, [isShareModalOpen, isShareReady, share])

  useEffect(() => {
    if (!isShareModalOpen) {
      setPendingAccessLevel(null)
    }
  }, [isShareModalOpen])

  const postAccessLevel = useCallback(async (newAccessLevel) => {
    if (newAccessLevel === RECAP_ACCESS_LEVELS.PRIVATE) {
      return true
    }

    try {
      const response = await postShares({
        event_uuid: trackedMeetingUuid,
        access_level: newAccessLevel,
      }).unwrap()

      if (newAccessLevel === RECAP_ACCESS_LEVELS.RESTRICTED) {
        invalidateRecapPermissionsCache(dispatch, trackedMeetingUuid)
      }

      setAccessLevel(newAccessLevel)
      setLocalShare(response)

      return true
    } catch {
      dispatch(showError(I18n.t('publisher.meeting_recap.share_modal.errors.update_access_level_failed')))
      return false
    }
  }, [dispatch, postShares, setLocalShare, trackedMeetingUuid])

  const deleteShare = useCallback(async () => {
    try {
      await destroyShares({ event_uuid: trackedMeetingUuid }).unwrap()
      setAccessLevel(RECAP_ACCESS_LEVELS.PRIVATE)
      setLocalShare(null)
      return true
    } catch {
      dispatch(showError(I18n.t('publisher.meeting_recap.share_modal.errors.update_access_level_failed')))
      return false
    }
  }, [destroyShares, dispatch, setLocalShare, trackedMeetingUuid])


  const handleAccessLevelChange = useCallback((newAccessLevel) => {
    // Only owners can change access level
    if (recapPermission !== RecapPermission.OWNER) {
      return
    }

    // No change
    if (newAccessLevel === accessLevel) {
      return
    }

    if (newAccessLevel === RECAP_ACCESS_LEVELS.RESTRICTED) {
      postAccessLevel(newAccessLevel)
      return
    }

    if (accessLevel === RECAP_ACCESS_LEVELS.PRIVATE) {
      postAccessLevel(newAccessLevel)
      return
    }

    setPendingAccessLevel(newAccessLevel)
  }, [accessLevel, recapPermission, postAccessLevel])

  const dismissPendingAccessLevelChange = useCallback(() => {
    setPendingAccessLevel(null)
  }, [])

  // Invalidate permissions cache when leaving Restricted.
  const confirmPendingAccessLevelChange = useCallback(async () => {
    if (pendingAccessLevel === RECAP_ACCESS_LEVELS.PRIVATE) {
      const didDeleteShare = await deleteShare()

      if (!didDeleteShare) {
        return false
      }

      invalidateRecapPermissionsCache(dispatch, trackedMeetingUuid)
      setPendingAccessLevel(null)
      return true
    }

    if (isRecapPublic(pendingAccessLevel)) {
      const didSavePublicAccess = await postAccessLevel(RECAP_ACCESS_LEVELS.PUBLIC)

      if (!didSavePublicAccess) {
        return false
      }

      invalidateRecapPermissionsCache(dispatch, trackedMeetingUuid)
      setPendingAccessLevel(null)
      return true
    }

    return false
  }, [deleteShare, dispatch, pendingAccessLevel, postAccessLevel, trackedMeetingUuid])

  return {
    value: accessLevel,
    pendingValue: pendingAccessLevel,
    isSubmitting: isAccessLevelSubmitting,
    onChange: handleAccessLevelChange,
    confirmPending: confirmPendingAccessLevelChange,
    dismissPending: dismissPendingAccessLevelChange,
  }
}
